import React, { useState, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTheme, Container, TextField, Button, Stack, Box, Autocomplete, Typography, Modal, CircularProgress, Stepper, Step, StepLabel, StepContent } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Controller, SubmitHandler, useForm, FormProvider } from 'react-hook-form';
import { useAmaWorkflowContext } from '../../../../context/ama-workflow-provider';
import { EditMode, FileStatus } from '../../../../data/enums';
import { DevTool } from '@hookform/devtools';
import ManifestFileUploader from '../manifest-file-uploader';
import FileUploaderItemStyleTester from '../file-uploader-item-style-tester';
import { useS3Manager } from '../../../../hooks/useS3Manager';
import { integer } from 'aws-sdk/clients/cloudfront';

interface IFormInputs {
  ClientId: string;
  SnapshotDate: string;
}

interface IClientXtd extends IClient {
  label: string;
}

interface ISnapshotAddUpdateProps {
  mode: EditMode;
  setEditMode: React.Dispatch<React.SetStateAction<EditMode>>;
};

export default function SnapshotAddUpdate(props: ISnapshotAddUpdateProps) {

  const theme = useTheme();
  const { clients, loadSnapshots, addSnapshot, deleteSnapshotFile, selectedSnapshot, setSelectedSnapshot, snapshotsError, clearSnapshotErrors, snapshotsLoading } = useAmaWorkflowContext();  
  const formProps = useForm<IFormInputs>({ defaultValues: getDefaultFormValues(selectedSnapshot) });  
  const [selectedClient, setSelectedClient] = useState<IClientXtd | null>(null);
  const [snapshotDate, setSnapshotDate] = useState<Dayjs | null>(null);
  const [cleared, setCleared] = useState<boolean>(false);  
  const [isUploading, setIsUploading] = useState<boolean>(false);  
  const [saveStep, setSaveStep] = useState<integer>(0);
  const [snapshotCategoryForDelete, setSnapshotCategoryForDelete] = useState<string | null>(null);
  
  const { fileUploads, setFileForUpload, uploadAll } = useS3Manager({
    bucketName: process.env.REACT_APP_AWS_S3_UPLOAD_BUCKET!, 
    manifestItems: (selectedSnapshot) ? selectedSnapshot?.SourceFiles : selectedClient?.AmaPipeline?.SourceFiles,
    selectedClient: formProps.getValues('ClientId'),
    snapshotDate: formProps.getValues('SnapshotDate') });  //TODO: replace with selectedClient.ClientId, selectedClient is only init on add snapshot

  useEffect(() => {
    if (cleared) {
      const timeout = setTimeout(() => {
        setCleared(false);
      }, 1500);

      return () => clearTimeout(timeout);
    }
  }, [cleared]);

  const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs, e) => {

    // Saving interstitial - step 0
    setSaveStep(0);

    // If edit mode is ADD we need to save and return from successful PUT
    if (props.mode === EditMode.Add) {

      // Add new snapshot
      const success = await addSnapshot(data.ClientId, data.SnapshotDate);

      // On error, modal will be shown, do not continue with uploads
      if (!success) return;
    }

    // Upload snapshot files
    if (fileUploads) {

      // Get uploads for PUT
      const pendingUploads = fileUploads.filter(item => item.FileStatus === FileStatus.Collected || item.FileStatus === FileStatus.Uploading);
      if (pendingUploads.length > 0) {
        
        // Saving interstitial - step 1
        setSaveStep(1);
        setIsUploading(true);

        // Save to S3 preprocessing bucket
        await uploadAll();

        // Add slight delay, allow files to hit back-end and update state (so we get latest status when we reload snapshots)
        await delay(3000);
      }
    }

    // Reload snapshots
    await loadSnapshots();

    // Return to list view and deselect snapshot
    setSaveStep(2);
    setSelectedSnapshot(null, null);
    props.setEditMode(EditMode.View);    
  };

  const handleDeleteFileButtonClick = (category: string) => {
    setSnapshotCategoryForDelete(category);
  };

  const handleDeleteFileModalOkButtonClick = async () => {
    
    if (!selectedSnapshot || !snapshotCategoryForDelete) return;

    // We want to imediately close modal and use loading interstitial while async api call is made
    const category = snapshotCategoryForDelete;
    setSnapshotCategoryForDelete(null);

    // API call
    const success = await deleteSnapshotFile(
      selectedSnapshot.ClientId!, 
      selectedSnapshot.SnapshotDate!, 
      snapshotCategoryForDelete!);    
    
    // Update file state to empty
    if (success) setFileForUpload(category, null);
  };

  const handleDeleteFileModalCancelButtonClick = () => {
    setSnapshotCategoryForDelete(null);
  };

  const handleCancelButtonClick = () => {
    setSelectedSnapshot(null, null);
    props.setEditMode(EditMode.View);    
  };

  const handleModalOkButtonClick = () => {
    clearSnapshotErrors();
  };

  const LogUploadDetails = () => {
    if (!hasPendingUploads()) return;
    const pendingUploads =  fileUploads?.filter(item => item.FileStatus === FileStatus.Collected);
    if (pendingUploads && pendingUploads.length > 0){
      console.log('Count: ' + pendingUploads.length);
      pendingUploads.forEach((item)=> { console.log('Category: ' + item.Category + '; Name: ' + item.FileForUpload?.name + '; Size: ' + item.FileForUpload?.size + ' bytes' )});
    }
  };

  const logFormErrors = () => {
    console.log(`SnapshotAddUpdate.DEBUG :: client: ${selectedClient?.ClientId}, form errors: ${formProps.formState.errors}`);
  }

  const handleClientSelection = (event: any, client: IClientXtd | null) => {
    setSelectedClient(client);
    formProps.setValue('ClientId', client?.ClientId || '');
    if (client) formProps.clearErrors('ClientId');
  };

  const handleSnapshotDateSelection = (newDate: dayjs.Dayjs | null) => {
    setSnapshotDate(newDate);
    formProps.setValue('SnapshotDate', newDate ? newDate.format('YYYYMMDD') : '');
    if (newDate) formProps.clearErrors('SnapshotDate');
  };

  const getSaveButtonLabel_v2 = () : string => {
    if (hasPendingUploads()) {
      if (props.mode === EditMode.Add) return 'SAVE AND UPLOAD';
      return 'UPLOAD';
    } 

    return 'SAVE';
  };

  const hasPendingUploads = () : boolean => {
    if (!fileUploads) return false;
    if (fileUploads.length == 0) return false;
    return fileUploads.filter(item => item.FileStatus === FileStatus.Collected).length > 0;
  };

  const hasUploadingUploads = () : boolean => {
    if (!fileUploads) return false;
    if (fileUploads.length == 0) return false;
    return fileUploads.filter(item => item.FileStatus === FileStatus.Uploading).length > 0;
  };

  const delay = (timeMs: number) => {
    return new Promise(resolve => setTimeout(resolve, timeMs));
  }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box id="inner" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: '600px' }}>
        <FormProvider {...formProps}>
          <form 
            noValidate 
            onSubmit={formProps.handleSubmit(onSubmit)}>

            {/* Loading overlay */}
            {(snapshotsLoading || isUploading) && (
              <Box 
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  background: 'rgba(255, 255, 255, 0.6)',
                  zIndex: 999,              
                }}>

                <Box 
                  sx={{ 
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    boxShadow: 24,
                    p: 4
                  }}>
                
                  <Stepper activeStep={saveStep} orientation="vertical" sx={{ width:'350px', backgroundColor:'white'}}>
                    {saveSteps.map((step, index) => (
                      <Step key={step.label}>
                        <StepLabel>
                          <Typography variant='h6' sx={{ mx: 2 }} color="text.secondary">
                            {step.label}
                          </Typography>
                        </StepLabel>
                        <StepContent>
                        <Typography variant='body1'  sx={{ mx: 2 }} color="text.secondary">
                            {step.description}
                          </Typography>
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </Box>
            )}

            {/* Fixed width content wrapper that centers */}
            <Container sx={{ width: '600px', margin: 0, padding: 0, mb: 10 }}>

              {/* Section header */}
              <Typography component="h6" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Snapshot Details
              </Typography>

              <Stack spacing={2}>

                {/* Mode: Edit */}
                {(props.mode === EditMode.Edit) &&
                  <>
                    <TextField 
                      label='Client ID'
                      type='text'
                      disabled={true}
                      {...formProps.register('ClientId')}
                    />
                    <TextField 
                      label='Snapshot Date'
                      type='text'
                      disabled={true}
                      {...formProps.register('SnapshotDate')}
                    />  
                  </>
                }

                {/* Mode: Add */}     
                {(props.mode === EditMode.Add) &&
                  <>
                    <Controller
                      name="ClientId"
                      control={formProps.control}
                      rules={{ required: 'Client selection is required.' }}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          disablePortal
                          id="ClientId"
                          options={mapClientsXtd(clients)}
                          sx={{ width: '100%'}}
                          value={selectedClient}
                          onChange={handleClientSelection}
                          isOptionEqualToValue={(option, value) => option.ClientId === value.ClientId}
                          renderInput={(params) => 
                            <TextField
                              label="Client ID"
                              type='text'
                              error={Boolean(formProps.formState.errors.ClientId)}
                              helperText={formProps.formState.errors.ClientId?.message}
                              {...params} 
                            /> 
                          }
                        />
                      )}
                    />
                    <Controller
                      name="SnapshotDate"
                      control={formProps.control}
                      rules={{ required: 'Snapshot date is required.' }}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          label={'Snapshot Date'}
                          value={snapshotDate}
                          onChange={handleSnapshotDateSelection}
                          views={['year', 'month', 'day']}
                          format='YYYY/MM/DD'
                          slotProps={{
                            field: { 
                              clearable: true, 
                              onClear: () => setCleared(true) 
                            },
                            textField: {
                              type: 'text',
                              error: Boolean(formProps.formState.errors.SnapshotDate),
                              helperText: formProps.formState.errors.SnapshotDate?.message
                            }
                          }}
                        />
                      )}
                    />
                  </>
                }

                {/* Manifest file uploader */}
                { fileUploads && fileUploads.length > 0 &&
                  <>
                    <Typography component="h6" sx={{ color: theme.palette.text.secondary, mb: 2, pt: 2 }}>
                      Snapshot Files
                    </Typography>
                    <ManifestFileUploader 
                      Items={fileUploads} 
                      SetFileForUpload={setFileForUpload}
                      DeleteFile={handleDeleteFileButtonClick}/>
                  </>
                }

                {/* Save/Cancel buttons */}
                <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end" sx={{ pt: 4}}>
                  <Button type='submit' variant='contained' color='primary'>{getSaveButtonLabel_v2()}</Button>
                  <Button variant='outlined' color='primary' onClick={handleCancelButtonClick}>Cancel</Button>
                </Stack>   

              </Stack>

              {/* Test File Item templates */}
              {/* <br/>
              <FileUploaderItemStyleTester />  */}

              {/* Remove when dev complete */}
              <DevTool control={formProps.control}/>
                    
            </Container>
          </form>
        </FormProvider>

        {/* Error modal */}
        <Modal
          open={Boolean(snapshotsError)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,}}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Error
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
              {snapshotsError}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="primary" onClick={handleModalOkButtonClick}>Ok</Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete snapshot file confirmation modal */}
        <Modal
          open={Boolean(snapshotCategoryForDelete)}
          aria-labelledby="modal-file-delete"
          aria-describedby="modal-file-delete">
          <Box sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,}}>
            <Typography id="modal-delete-file-title" variant="h6" component="h2">
              Delete
            </Typography>
            <Typography id="modal-delete-file-description" sx={{ mt: 2, mb: 4 }}>
              Are you sure you want to delete the {snapshotCategoryForDelete} file in Bronze? If any Silver job artifacts exist, they will also be deleted.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="primary" sx={{mr: 2}} onClick={handleDeleteFileModalOkButtonClick}>Ok</Button>
              <Button variant="outlined" color="primary" onClick={handleDeleteFileModalCancelButtonClick}>Cancel</Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
}

const getDefaultFormValues = (snapshot: ISnapshot | null): IFormInputs => {
  
  if (snapshot) {
    return {
      ClientId: snapshot.ClientId,
      SnapshotDate: snapshot.SnapshotDate }     
  }
  
  return {
    ClientId: '',
    SnapshotDate: ''}
};

const mapClientsXtd = (clients: Array<IClient> | null) : Array<IClientXtd> => {
  if (!clients) return [];
  const activeClients = clients.filter(client => client.IsActive === true);
  return activeClients.map(client => ({
    ...client,
    label: `${client.ClientId}`
  }));
};

const saveSteps = [
  {
    label: 'Saving snapshot',
    description: 'We need to update the AWS resources associated with your ETL pipeline.'
  },
  {
    label: 'Uploading files',
    description: 'We are uploading your files for preprocessing. If they are successfully ingested they will be available in Bronze.'
  }
];